/**
 * Installer-token RPC middleware tags (client-safe).
 *
 * Lives alongside `auth.tags.ts` and follows the same client/server
 * split — only the `Context.Tag` / `RpcMiddleware.Tag` declarations
 * belong here. All backend code (hash lookup, DB queries) is in
 * `installer.middleware.ts`.
 *
 * @since 1.0.0
 */
import { RpcMiddleware } from "@effect/rpc";
import { Context, Schema } from "effect";

/**
 * The caller context installed by `InstallerTokenMiddleware` after a
 * successful bearer-token verification.
 * @since 1.0.0
 */
export class InstallerTokenAuth extends Context.Tag("InstallerTokenAuth")<
  InstallerTokenAuth,
  {
    readonly onboardingId: string;
    readonly scopes: ReadonlyArray<string>;
  }
>() {}

/**
 * Wire error returned by the middleware when the caller fails to
 * present a valid token.
 * @since 1.0.0
 */
export class InstallerUnauthorized extends Schema.TaggedError<InstallerUnauthorized>()(
  "InstallerUnauthorized",
  {
    reason: Schema.Literal("missing", "invalid", "expired", "revoked"),
    message: Schema.String,
  },
) {}

/**
 * RPC middleware guarding producer endpoints — Mac binary installer
 * and Hetzner cluster-workflow bun binary use this. Tokens are
 * short-lived and scoped to a single onboarding.
 * @since 1.0.0
 */
export class InstallerTokenMiddleware extends RpcMiddleware.Tag<InstallerTokenMiddleware>()(
  "InstallerTokenMiddleware",
  {
    provides: InstallerTokenAuth,
    failure: InstallerUnauthorized,
    requiredForClient: true,
  },
) {}
